import { ConnectError } from '@connectrpc/connect'
import {
  AreaChangeRegistrationKind,
  GenerationSiteAreaChangeNeedsConfirmationSchema,
  PlantAreaChangeBlockedByRegistrationSchema,
  PlantAreaChangeNeedsConfirmationSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import { convertAreaLabel } from '~/models/area'

/**
 * エリア変更の歯止め (系統コード・発電所の更新) が返す型つきの error detail を、画面の文言・確認の中身にする。
 * 登録先・ユニットの一覧は repeated field で届く (区切り文字の文字列を解釈しない)。
 */

/** 系統コードのエリア変更を止める文言 (登録先を並べ、先に外すよう案内する)。止められていなければ undefined。 */
export function plantAreaChangeBlockedMessage(
  t: TFunction,
  err: unknown,
): string | undefined {
  if (!(err instanceof ConnectError)) return undefined
  const detail = err.findDetails(PlantAreaChangeBlockedByRegistrationSchema)[0]
  if (detail === undefined) return undefined
  const targets = detail.registrations.map((registration) => {
    switch (registration.kind) {
      case AreaChangeRegistrationKind.BG:
        return t('page:adminPlantUnitMaster.areaChange.target.bg', {
          code: registration.code,
        })
      case AreaChangeRegistrationKind.GENERATION_SALES_MASTER:
        return t(
          'page:adminPlantUnitMaster.areaChange.target.generationSalesMaster',
          {
            area: convertAreaLabel(registration.areaCode),
            code: registration.code,
          },
        )
      default:
        // 未知の種類 (UNSPECIFIED・将来の追加) は既知の種類に倒さず、種類を言わずにコードだけ出す
        return t('page:adminPlantUnitMaster.areaChange.target.unknown', {
          code: registration.code,
        })
    }
  })
  return t('page:adminPlantUnitMaster.areaChange.blocked', {
    plantCode: detail.plantCode,
    targets: targets.join(
      t('page:adminPlantUnitMaster.areaChange.targetJoiner'),
    ),
  })
}

/** 確認ダイアログに出す内容。 */
export type AreaChangeConfirmation = {
  title: string
  description: string
  units: string[]
}

/**
 * 系統コードの更新が「確認が要る」で止まった時の確認ダイアログの中身
 * (ユニットの発電所のエリアも直すよう案内する)。確認の要るエラーでなければ undefined。
 */
export function plantAreaChangeConfirmationOf(
  t: TFunction,
  err: unknown,
): AreaChangeConfirmation | undefined {
  if (!(err instanceof ConnectError)) return undefined
  const detail = err.findDetails(PlantAreaChangeNeedsConfirmationSchema)[0]
  if (detail === undefined) return undefined
  return {
    title: t('page:adminPlantUnitMaster.areaChange.plantConfirm.title'),
    description: t('page:adminPlantUnitMaster.areaChange.plantConfirm.body', {
      plantCode: detail.plantCode,
      area: convertAreaLabel(detail.toAreaCode),
    }),
    units: detail.units.map((unit) =>
      t('page:adminPlantUnitMaster.areaChange.plantConfirm.unit', {
        unit: unit.unitName,
        site: unit.siteName,
        area: convertAreaLabel(unit.siteAreaCode),
      }),
    ),
  }
}

/** 発電所の更新が「確認が要る」で止まった時の確認ダイアログの中身 (系統コードのエリアも直すよう案内する)。 */
export function siteAreaChangeConfirmationOf(
  t: TFunction,
  err: unknown,
): AreaChangeConfirmation | undefined {
  if (!(err instanceof ConnectError)) return undefined
  const detail = err.findDetails(
    GenerationSiteAreaChangeNeedsConfirmationSchema,
  )[0]
  if (detail === undefined) return undefined
  return {
    title: t('page:adminPlantUnitMaster.areaChange.siteConfirm.title'),
    description: t('page:adminPlantUnitMaster.areaChange.siteConfirm.body', {
      site: detail.siteName,
      area: convertAreaLabel(detail.toAreaCode),
    }),
    units: detail.units.map((unit) =>
      t('page:adminPlantUnitMaster.areaChange.siteConfirm.unit', {
        unit: unit.unitName,
        plantCode: unit.plantCode,
        area: convertAreaLabel(unit.plantAreaCode),
      }),
    ),
  }
}
